import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, CheckCircle, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NewPurchasePage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [constants, setConstants] = useState({ payment_conditions: [] });
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    proveedor_id: '',
    numero_factura: '',
    fecha_compra: new Date().toISOString().split('T')[0],
    fecha_recepcion: '',
    condicion_pago: '',
    dias_credito_personalizado: '',
    fecha_vencimiento_personalizada: '',
    descuento: 0,
    notas: ''
  });

  const [lineas, setLineas] = useState([]);
  const [currentLine, setCurrentLine] = useState({
    producto_id: '',
    cantidad: '',
    costo_unitario: ''
  });

  // Fetch data
  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/suppliers/?is_active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, [token]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inventory/products?is_active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [token]);

  const fetchConstants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/purchases/constants`);
      setConstants(response.data);
    } catch (error) {
      console.error('Error fetching constants:', error);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    fetchConstants();
  }, [fetchSuppliers, fetchProducts, fetchConstants]);

  // When supplier changes, set default payment condition
  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setFormData(prev => ({
      ...prev,
      proveedor_id: supplierId,
      condicion_pago: supplier?.condicion_pago_default || 'contado'
    }));
  };

  // Add line
  const handleAddLine = () => {
    if (!currentLine.producto_id || !currentLine.cantidad || !currentLine.costo_unitario) {
      toast.error('Complete todos los campos de la línea');
      return;
    }

    const producto = products.find(p => p.id === currentLine.producto_id);
    const cantidad = parseInt(currentLine.cantidad);
    const costo = parseFloat(currentLine.costo_unitario);

    // Check if product already in lines
    const existingIndex = lineas.findIndex(l => l.producto_id === currentLine.producto_id);
    if (existingIndex >= 0) {
      toast.error('Este producto ya está en la lista. Elimínelo primero si desea modificarlo.');
      return;
    }

    setLineas(prev => [...prev, {
      producto_id: currentLine.producto_id,
      producto_codigo: producto?.codigo || '',
      producto_nombre: producto?.nombre_comercial || '',
      cantidad: cantidad,
      costo_unitario: costo,
      subtotal: cantidad * costo
    }]);

    setCurrentLine({ producto_id: '', cantidad: '', costo_unitario: '' });
  };

  // Remove line
  const handleRemoveLine = (index) => {
    setLineas(prev => prev.filter((_, i) => i !== index));
  };

  // Calculate totals
  const subtotal = lineas.reduce((sum, l) => sum + l.subtotal, 0);
  const descuento = parseFloat(formData.descuento) || 0;
  const total = subtotal - descuento;

  // Get last cost for product
  const getLastCost = (productoId) => {
    const producto = products.find(p => p.id === productoId);
    return producto?.precio_costo_promedio || 0;
  };

  // Handle product selection - suggest last cost
  const handleProductSelect = (productoId) => {
    const lastCost = getLastCost(productoId);
    setCurrentLine(prev => ({
      ...prev,
      producto_id: productoId,
      costo_unitario: lastCost > 0 ? lastCost.toFixed(2) : ''
    }));
  };

  // Save purchase
  const handleSave = async (asBorrador = false) => {
    if (!formData.proveedor_id) {
      toast.error('Seleccione un proveedor');
      return;
    }
    if (lineas.length === 0) {
      toast.error('Agregue al menos un producto');
      return;
    }
    if (!formData.fecha_compra) {
      toast.error('Ingrese la fecha de compra');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        proveedor_id: formData.proveedor_id,
        numero_factura: formData.numero_factura || null,
        fecha_compra: formData.fecha_compra,
        fecha_recepcion: formData.fecha_recepcion || null,
        condicion_pago: formData.condicion_pago || 'contado',
        dias_credito_personalizado: formData.condicion_pago === 'credito_personalizado' && formData.dias_credito_personalizado 
          ? parseInt(formData.dias_credito_personalizado) : null,
        fecha_vencimiento_personalizada: formData.condicion_pago === 'credito_personalizado' 
          ? formData.fecha_vencimiento_personalizada || null : null,
        descuento: descuento,
        lineas: lineas.map(l => ({
          producto_id: l.producto_id,
          cantidad: l.cantidad,
          costo_unitario: l.costo_unitario
        })),
        notas: formData.notas || null,
        es_borrador: asBorrador
      };

      await axios.post(`${API}/purchases/`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(asBorrador ? 'Borrador guardado' : 'Compra confirmada');
      navigate('/admin/compras');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => `Q ${(value || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/compras')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="font-display text-2xl text-slate-900">Nueva Compra</h1>
          <p className="text-slate-500 text-sm">Registrar factura de proveedor</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="col-span-2 space-y-6">
          {/* Supplier & Invoice Info */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Información de la Compra</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Proveedor *</Label>
                  <Select value={formData.proveedor_id} onValueChange={handleSupplierChange}>
                    <SelectTrigger data-testid="supplier-select">
                      <SelectValue placeholder="Seleccionar proveedor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.nombre_comercial} {s.nit && `(${s.nit})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>No. Factura del Proveedor</Label>
                  <Input
                    value={formData.numero_factura}
                    onChange={(e) => setFormData(prev => ({ ...prev, numero_factura: e.target.value }))}
                    placeholder="Número de factura"
                    data-testid="invoice-number"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha de Compra *</Label>
                  <Input
                    type="date"
                    value={formData.fecha_compra}
                    onChange={(e) => setFormData(prev => ({ ...prev, fecha_compra: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Fecha de Recepción</Label>
                  <Input
                    type="date"
                    value={formData.fecha_recepcion}
                    onChange={(e) => setFormData(prev => ({ ...prev, fecha_recepcion: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Condición de Pago</Label>
                  <Select 
                    value={formData.condicion_pago} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, condicion_pago: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {constants.payment_conditions.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.condicion_pago === 'credito_personalizado' && (
                  <div>
                    <Label>Días de crédito o fecha</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={formData.dias_credito_personalizado}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          dias_credito_personalizado: e.target.value,
                          fecha_vencimiento_personalizada: ''
                        }))}
                        placeholder="Días"
                        className="w-24"
                      />
                      <span className="self-center text-slate-500">o</span>
                      <Input
                        type="date"
                        value={formData.fecha_vencimiento_personalizada}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          fecha_vencimiento_personalizada: e.target.value,
                          dias_credito_personalizado: ''
                        }))}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Product Lines */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5" />
                Productos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Add Line Form */}
              <div className="grid grid-cols-12 gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                <div className="col-span-5">
                  <Select value={currentLine.producto_id} onValueChange={handleProductSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar producto..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.codigo} - {p.nombre_comercial}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    min="1"
                    value={currentLine.cantidad}
                    onChange={(e) => setCurrentLine(prev => ({ ...prev, cantidad: e.target.value }))}
                    placeholder="Cantidad"
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentLine.costo_unitario}
                    onChange={(e) => setCurrentLine(prev => ({ ...prev, costo_unitario: e.target.value }))}
                    placeholder="Costo unit. (Q)"
                  />
                </div>
                <div className="col-span-2">
                  <Button onClick={handleAddLine} className="w-full">
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar
                  </Button>
                </div>
              </div>

              {/* Lines Table */}
              {lineas.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-3">Código</th>
                        <th className="text-left p-3">Producto</th>
                        <th className="text-right p-3">Cantidad</th>
                        <th className="text-right p-3">Costo Unit.</th>
                        <th className="text-right p-3">Subtotal</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineas.map((linea, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="p-3 font-mono text-slate-500">{linea.producto_codigo}</td>
                          <td className="p-3">{linea.producto_nombre}</td>
                          <td className="p-3 text-right">{linea.cantidad}</td>
                          <td className="p-3 text-right">{formatCurrency(linea.costo_unitario)}</td>
                          <td className="p-3 text-right font-medium">{formatCurrency(linea.subtotal)}</td>
                          <td className="p-3">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemoveLine(idx)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-lg">
                  Agregue productos a la compra
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardContent className="pt-6">
              <Label>Notas</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="Observaciones adicionales..."
                rows={2}
              />
            </CardContent>
          </Card>
        </div>

        {/* Summary Sidebar */}
        <div className="space-y-4">
          <Card className="sticky top-6">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Resumen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal:</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between items-center">
                <Label className="text-slate-500">Descuento (Q):</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.descuento}
                  onChange={(e) => setFormData(prev => ({ ...prev, descuento: e.target.value }))}
                  className="w-28 text-right"
                />
              </div>
              <div className="flex justify-between text-lg font-bold pt-4 border-t">
                <span>Total:</span>
                <span className="text-green-700">{formatCurrency(total)}</span>
              </div>

              {/* Payment Info Preview */}
              {formData.condicion_pago && formData.condicion_pago !== 'contado' && (
                <div className="p-3 bg-amber-50 rounded-lg text-sm">
                  <div className="font-medium text-amber-800">Cuenta por Pagar</div>
                  <div className="text-amber-600">
                    {constants.payment_conditions.find(c => c.value === formData.condicion_pago)?.label}
                  </div>
                </div>
              )}

              {formData.condicion_pago === 'contado' && (
                <div className="p-3 bg-green-50 rounded-lg text-sm">
                  <div className="font-medium text-green-800">Pago al Contado</div>
                  <div className="text-green-600">Se registrará como pagada</div>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2 pt-4">
                <Button
                  onClick={() => handleSave(false)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  disabled={saving || lineas.length === 0}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {saving ? 'Guardando...' : 'Confirmar Compra'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSave(true)}
                  className="w-full"
                  disabled={saving || lineas.length === 0}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Guardar como Borrador
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NewPurchasePage;
